import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
// Components
import App from '../../../components/app';
import EnhancedTable from '../../../components/table';
import ErrorMessage from '../../../components/error-message';
// Modules
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { actions as vehicleActions, selectors as vehicleSelectors } from '../../../redux/reducers/vehicles';
import { actions as partnerActions, selectors as partnerSelectors } from '../../../redux/reducers/partners';
import { LightStructure, VehicleTypeDetails } from '../../../redux/reducers/api-types';
// Ressources
import FormattingUtils from '../../../utils/formattingUtils';
import { generateOptions } from '../../../utils/businessUtils';
import { FormatType, HeadCell, PickerOption } from '../../../local-types';
import { Cell, createCell } from '../../../components/table/table-cell-creator';

interface VehiclesListProps extends WithTranslation, RouteComponentProps {
    vehicleMotorizations: LightStructure[];
    types: VehicleTypeDetails[];
    getVehicleTypes: () => void;
    getPartnerMotorizations: () => void;
    getVehicleTypesError: ErrorPayload;
    getVehicleModelsMotorizationAndColorsError: ErrorPayload;
}

class VehiclesListPage extends React.Component<VehiclesListProps> {
    componentDidMount() {
        this.props.getPartnerMotorizations();

        const { types, getVehicleTypes } = this.props;
        if (types.length === 0) {
            getVehicleTypes();
        }
    }

    generateMotorizationOptions = (): PickerOption[] => {
        const { vehicleMotorizations } = this.props;
        return generateOptions(vehicleMotorizations, 'name');
    };

    generateTypeOptions = (): PickerOption[] => {
        const { types } = this.props;
        return generateOptions(types, 'localized_name.fr', 'localized_name.fr');
    };

    headerBuilder = (): HeadCell[] => {
        const { t } = this.props;
        const headers: HeadCell[] = [
            createCell(t, Cell.ID, { sortable: true }),
            { id: 'vehicle_model.brand.name', label: t('brand'), contains: false },
            {
                id: 'vehicle_model.localized_string.fr',
                label: t('model'),
                contains: false,
            },
            {
                id: 'vehicle_color.localized_string.fr',
                label: t('color'),
                contains: false,
            },
            {
                id: 'registration_number',
                label: t('registration_number'),
                type: FormatType.Registration,
                contains: false,
            },
            {
                id: 'immatriculation_date',
                label: t('immatriculation_date'),
                type: FormatType.DateShort,
                filterable: false,
                formatCell: function (row) {
                    const greyCard = row.documents.find((d) => d.type === 'grey_card');
                    const fallbackDate = row.immatriculation_date
                        ? FormattingUtils.format(row.immatriculation_date, FormatType.DateShort, t)
                        : null;

                    return (
                        <>
                            {greyCard && greyCard.current_version ? (
                                <div key={greyCard.id} style={{ marginLeft: 8 }}>
                                    {FormattingUtils.format(
                                        greyCard.current_version.start_date,
                                        FormatType.DateShort,
                                        t,
                                    )}
                                </div>
                            ) : (
                                fallbackDate
                            )}
                        </>
                    );
                },
            },
            {
                id: 'insurance_validity_date',
                label: t('insurance_validity_date'),
                type: FormatType.DateShort,
                filterable: false,
                formatCell: function (row) {
                    const greenCard = row.documents.find((d) => d.type === 'green_card');
                    const fallbackDate = row.insurance_validity_date
                        ? FormattingUtils.format(row.insurance_validity_date, FormatType.DateShort, t)
                        : null;

                    return (
                        <>
                            {greenCard && greenCard.current_version ? (
                                <div key={greenCard.id} style={{ marginLeft: 8 }}>
                                    {FormattingUtils.format(
                                        greenCard.current_version.end_date,
                                        FormatType.DateShort,
                                        t,
                                    )}
                                </div>
                            ) : (
                                fallbackDate
                            )}
                        </>
                    );
                },
            },
            {
                id: 'partner.name',
                label: t('common:form.partner'),
                formatCell: function (row) {
                    return (
                        <>
                            {row.partner && row.partner.name ? (
                                <div key={row.partner.id} style={{ marginLeft: 8, fontStyle: 'italic' }}>
                                    {row.partner.name}
                                </div>
                            ) : null}
                        </>
                    );
                },
            },
            {
                id: 'deletion_date',
                label: t('suspension'),
                type: FormatType.BooleanChips,
                chipsTest: (row, val) => !val,
                chipsOptions: [t('common:active'), t('common:suspended')],
                filterable: false,
            },
            {
                id: 'vehicle_model.type.localized_name.fr',
                label: t('type'),
                type: FormatType.Picker,
                pickerOptions: this.generateTypeOptions(),
                // formatCell: function (row) {
                //     return (
                //         <>
                //             {row.dispatch_groups && (
                //                 <div key={row.id}>{row.dispatch_groups.map(({ name }) => name).join(', ')}</div>
                //             )}
                //         </>
                //     );
                // },
            },
            {
                id: 'controlTower.name',
                label: t('control_tower'),
                formatCell: function (row) {
                    return (
                        <>
                            {row.controlTower && row.controlTower.name ? (
                                <div key={row.controlTower.id} style={{ marginLeft: 8, fontStyle: 'italic' }}>
                                    {row.controlTower.name}
                                </div>
                            ) : null}
                        </>
                    );
                },
            },
            {
                id: 'vehicle_motorization.localized_string.fr',
                label: t('motorization'),
                contains: false,
                type: FormatType.Picker,
                pickerOptions: this.generateMotorizationOptions(),
            },
        ];
        return headers;
    };

    render() {
        const { t, getVehicleModelsMotorizationAndColorsError, getVehicleTypesError } = this.props;

        return (
            <App
                disableBreadcrumb
                header={{
                    title: t('title'),
                    subtitle: t('description'),
                }}
            >
                <ErrorMessage payload={getVehicleModelsMotorizationAndColorsError || getVehicleTypesError} />
                <EnhancedTable headers={this.headerBuilder()} type={tableRequestPoints.VEHICLES} />
            </App>
        );
    }
}

const VehiclesListPageWithTranslation = withTranslation(['vehicle', 'common'])(VehiclesListPage);

const mapStateToProps = (state) => ({
    types: vehicleSelectors.types(state),
    vehicleMotorizations: partnerSelectors.vehicleMotorizations(state),
    getVehicleModelsMotorizationAndColorsError: errorSelectors.getVehicleModelsMotorizationAndColorsError(state),
    getVehicleTypesError: errorSelectors.getVehicleTypesError(state),
});

const mapDispatchToProps = {
    getVehicleTypes: vehicleActions.getVehicleTypes,
    getPartnerMotorizations: partnerActions.getPartnerMotorizations,
};

export default connect(mapStateToProps, mapDispatchToProps)(VehiclesListPageWithTranslation);
